/**
 * ============================================================================
 * useAvailableCalendarSources — Fontes do Calendário Global disponíveis
 * ----------------------------------------------------------------------------
 * Descrição:
 *   Fonte ÚNICA de verdade das fontes de evento que devem ser exibidas no
 *   Calendário Global (filtro "Filtrar por fonte", popover "Novo evento" e
 *   saneamento da seleção persistida em localStorage).
 *
 *   Evita que o usuário veja fontes de módulos que o tenant NÃO contratou
 *   (ex.: "Atividades CRM" / "Agendamento Online" sem o módulo CRM, ou
 *   "Tarefas" sem o módulo de Tarefas).
 *
 * Regras:
 *   - manual e scheduled_message: sempre disponíveis (recursos do core);
 *   - activity e booking: exigem o módulo CRM habilitado;
 *   - task: exige o módulo Tarefas habilitado;
 *   - service_order: exige o módulo Ordem de Serviço habilitado;
 *   - google: só quando existe ao menos um calendário vinculado ao Google.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-09 v1.1.0 - Inclui OS como fonte condicionada ao módulo.
 *   RELEASE: 2026-09-17 v1.0.0 - Criação (remove lista fixa de fontes).
 * ============================================================================
 */
import { useMemo } from 'react';
import { useHeaderModuleFlags } from '@/hooks/useHeaderModuleFlags';
import type { CalendarEventSourceType } from '@/hooks/useCalendarUnifiedEvents';
import { useServiceOrderModuleEnabled } from '@/hooks/useServiceOrderModule';

interface UseAvailableCalendarSourcesOptions {
  /** Indica se existe integração Google conectada (calendário com google_integration_id). */
  hasGoogleIntegration?: boolean;
}

export function useAvailableCalendarSources(
  options: UseAvailableCalendarSourcesOptions = {},
): CalendarEventSourceType[] {
  const { hasGoogleIntegration = false } = options;
  const { isCrmAvailable, isTasksAvailable } = useHeaderModuleFlags();
  const isServiceOrdersAvailable = useServiceOrderModuleEnabled();

  return useMemo(() => {
    const sources: CalendarEventSourceType[] = ['manual'];
    // Agendamento Online e Atividades pertencem ao módulo CRM.
    if (isCrmAvailable) sources.push('booking', 'activity');
    if (isTasksAvailable) sources.push('task');
    if (isServiceOrdersAvailable) sources.push('service_order');
    sources.push('scheduled_message');
    if (hasGoogleIntegration) sources.push('google');
    return sources;
  }, [isCrmAvailable, isTasksAvailable, isServiceOrdersAvailable, hasGoogleIntegration]);
}
